import {
  addDays,
  addHours,
  addMinutes,
  format,
  getHours,
  getMinutes,
  setHours,
  setMinutes,
  startOfHour,
} from 'date-fns';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import * as React from 'react';
import { Linking, Pressable, View } from 'react-native';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import type { Address } from '@/api/subscriptions/types';
import { DeliveryFeeModal } from '@/components/delivery-fee-modal';
import { isDesktop } from '@/core';
import useSearchStore from '@/stores/use-search-store';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Location } from '@/ui/icons/location';
import { Plus } from '@/ui/icons/plus';
import { Search as SearchIcon } from '@/ui/icons/search';

import { homeAssets } from './assets';
import { ImagePlaceholder } from './placeholder';

const minDate = startOfHour(addMinutes(addHours(new Date(), 3), 60));
const defaultEndDate = addDays(minDate, 3);

// HeroField's leading icon column. FIELD_TEXT_OFFSET is where its text starts,
// which the stacked mobile rows indent to so their content lines up.
const FIELD_PADDING_H = 12;
const FIELD_ICON_SIZE = 20;
const FIELD_ICON_GAP = 10;
const FIELD_TEXT_OFFSET = FIELD_PADDING_H + FIELD_ICON_SIZE + FIELD_ICON_GAP;

const ModeToggle = ({
  deliveryModeEnabled,
  onChange,
}: {
  deliveryModeEnabled: boolean;
  onChange: (delivery: boolean) => void;
}) => {
  const { theme } = useStyles();

  const renderPill = (label: string, active: boolean, onPress: () => void) => (
    <Pressable
      onPress={onPress}
      style={{
        paddingVertical: 8,
        paddingHorizontal: 20,
        borderRadius: 100,
        backgroundColor: active
          ? theme.colors.button.primary.default.fill
          : 'transparent',
      }}
    >
      <Text
        type="bodyRegular"
        style={{
          color: active
            ? theme.colors.button.primary.default.text
            : theme.colors.text.primary,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );

  return (
    <View style={{ flexDirection: 'row', gap: 4, alignSelf: 'flex-start' }}>
      {renderPill('Pickup', !deliveryModeEnabled, () => onChange(false))}
      {renderPill('Delivery', deliveryModeEnabled, () => onChange(true))}
    </View>
  );
};

// A rule between fields: vertical between the desktop columns, horizontal
// between the rows once the search bar stacks on mobile.
const FieldDivider = () => {
  const { theme } = useStyles();
  const desktop = isDesktop();

  return (
    <View
      style={{
        ...(desktop
          ? { width: 1, marginVertical: 8 }
          : { height: 1, marginHorizontal: 12 }),
        alignSelf: 'stretch',
        backgroundColor: theme.colors.card.default.borderColor,
      }}
    />
  );
};

const HeroField = ({
  icon,
  label,
  value,
  placeholder,
  onPress,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  placeholder: string;
  onPress: () => void;
  active?: boolean;
}) => {
  const { theme } = useStyles();

  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: FIELD_ICON_GAP,
        paddingVertical: 8,
        paddingHorizontal: FIELD_PADDING_H,
        borderRadius: 16,
        backgroundColor: active ? theme.colors.background_3 : 'transparent',
      }}
    >
      <Icon
        type="naked"
        icon={icon}
        fillColor={theme.colors.icon.nakedColor}
        width={FIELD_ICON_SIZE}
        height={FIELD_ICON_SIZE}
      />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text type="caption2" style={{ color: theme.colors.text.secondary }}>
          {label}
        </Text>
        <Text
          type="bodyRegular"
          numberOfLines={1}
          style={!value ? { color: theme.colors.text.secondary } : undefined}
        >
          {value || placeholder}
        </Text>
      </View>
    </Pressable>
  );
};

export const Hero = () => {
  const { theme } = useStyles();
  const router = useRouter();
  const desktop = isDesktop();

  const address = useSearchStore((state) => state.address);
  const startDatetime = useSearchStore((state) => state.startDatetime);
  const endDatetime = useSearchStore((state) => state.endDatetime);
  const setAddress = useSearchStore((state) => state.setAddress);
  const setStartDatetime = useSearchStore((state) => state.setStartDatetime);
  const setEndDatetime = useSearchStore((state) => state.setEndDatetime);
  const deliveryModeEnabled = useSearchStore(
    (state) => state.deliveryModeEnabled
  );
  const setDeliveryModeEnabled = useSearchStore(
    (state) => state.setDeliveryModeEnabled
  );
  const setDeliveryDeliverToAddress = useSearchStore(
    (state) => state.setDeliveryDeliverToAddress
  );
  const deliveryReturnAddress = useSearchStore(
    (state) => state.deliveryReturnAddress
  );
  const setDeliveryReturnToAddress = useSearchStore(
    (state) => state.setDeliveryReturnToAddress
  );
  const promo = useSettingsStore((state) => state.promoCode);

  const [showAutocomplete, setShowAutocomplete] = React.useState(false);
  const [showReturnField, setShowReturnField] = React.useState(
    !!deliveryReturnAddress
  );
  const [showReturnAutocomplete, setShowReturnAutocomplete] =
    React.useState(false);
  const [showStartCalendar, setShowStartCalendar] = React.useState(false);
  const [showEndCalendar, setShowEndCalendar] = React.useState(false);
  const [showFeeModal, setShowFeeModal] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');
  const containerRef = React.useRef<View>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !containerRef.current ||
        // @ts-ignore web-only DOM node
        containerRef.current.contains(event.target as Node)
      ) {
        return;
      }

      // Only the desktop calendar is an inline popover inside this container.
      // Below the desktop breakpoint it renders as a modal, which react-native-
      // web portals onto document.body, so presses on a day would read as
      // outside clicks and close the picker before Confirm can be tapped.
      if (desktop) {
        setShowStartCalendar(false);
        setShowEndCalendar(false);
      }

      setShowAutocomplete(false);
      setShowReturnAutocomplete(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [desktop]);

  const onSearch = () => {
    if (!address || !startDatetime || !endDatetime) {
      setErrorMsg('Please enter a specific location.');
      return;
    }

    // In delivery mode the return address defaults to the delivery address
    // unless the guest explicitly added a different return location.
    const returnAddress = (showReturnField && deliveryReturnAddress) || address;

    if (deliveryModeEnabled) {
      setDeliveryDeliverToAddress(address);
      setDeliveryReturnToAddress(returnAddress);
    }

    const paramsObj: Record<string, string> = {
      start_datetime: startDatetime.toISOString(),
      end_datetime: endDatetime.toISOString(),
      address: address.description,
      address_lat: String(address.latitude),
      address_lng: String(address.longitude),
    };

    if (deliveryModeEnabled) {
      paramsObj.mode = 'delivery';
      paramsObj.delivery_address = address.description;
      paramsObj.delivery_address_lat = String(address.latitude);
      paramsObj.delivery_address_lng = String(address.longitude);
      paramsObj.return_address = returnAddress.description;
      paramsObj.return_address_lat = String(returnAddress.latitude);
      paramsObj.return_address_lng = String(returnAddress.longitude);
    }

    if (promo) paramsObj.promo = promo as string;

    router.push(`/search?${new URLSearchParams(paramsObj).toString()}`);
  };

  const dateValue = (d: Date | null) =>
    d ? `${format(d, 'MMM d')} | ${format(d, 'h:mm a')}` : undefined;

  const calendarPopoverStyle = {
    width: 500,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 20,
  };

  const anyDropdownOpen =
    showStartCalendar ||
    showEndCalendar ||
    showAutocomplete ||
    showReturnAutocomplete;

  const deliveryFeeLink = (style: object) => (
    <Pressable
      onPress={() => setShowFeeModal(true)}
      style={{ position: 'relative', zIndex: 1, ...style }}
    >
      <Text
        type="caption1"
        style={{ color: theme.colors.button.link.default.text }}
      >
        How we calculate delivery fees
      </Text>
    </Pressable>
  );

  const searchBar = (
    <View
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: 1080,
        alignSelf: 'center',
        backgroundColor: theme.colors.background_2,
        borderRadius: 24,
        borderWidth: 1,
        borderColor: theme.colors.card.default.borderColor,
        padding: 16,
        gap: 12,
        // Lift the whole search bar above the app-store badges below it so the
        // calendar popovers render on top of them.
        position: 'relative',
        zIndex: 20,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
        <ModeToggle
          deliveryModeEnabled={deliveryModeEnabled}
          onChange={(delivery) => {
            setDeliveryModeEnabled(delivery);
            setErrorMsg('');
            if (!delivery) {
              setShowReturnField(false);
              setShowReturnAutocomplete(false);
              setDeliveryReturnToAddress(null);
            }
          }}
        />

        {deliveryModeEnabled && desktop
          ? deliveryFeeLink({ alignSelf: 'center' })
          : null}
      </View>

      <View
        style={{
          flexDirection: desktop ? 'row' : 'column',
          alignItems: desktop ? 'center' : 'stretch',
          gap: desktop ? 8 : 4,
          // Establish a stacking context that sits above the delivery-fee link
          // below, so autocomplete/calendar popovers render on top of it.
          position: 'relative',
          zIndex: 2,
        }}
      >
        <View
          style={{
            flex: desktop ? 1 : undefined,
            minWidth: 0,
            minHeight: 56,
            justifyContent: 'center',
            position: 'relative',
            zIndex: showAutocomplete ? 40 : 3,
          }}
        >
          {showAutocomplete ? (
            <GoogleAutocomplete
              label={
                deliveryModeEnabled ? 'Delivery Location' : 'Pickup Location'
              }
              autoFocus
              placeholder="Address, airport, city..."
              onPress={(data: Address) => {
                setAddress({
                  description: data.description,
                  placeId: data.placeId,
                  latitude: data.latitude,
                  longitude: data.longitude,
                });
                setShowAutocomplete(false);
              }}
            />
          ) : (
            <HeroField
              icon={<Location />}
              label={
                deliveryModeEnabled ? 'Delivery Location' : 'Pickup Location'
              }
              value={address?.description}
              placeholder="Enter Address"
              onPress={() => {
                setErrorMsg('');
                setShowStartCalendar(false);
                setShowEndCalendar(false);
                setShowAutocomplete(true);
              }}
            />
          )}
        </View>

        {/* The return location belongs with the delivery address, so they sit
            in one group with no divider between them. */}
        {deliveryModeEnabled ? null : <FieldDivider />}

        {deliveryModeEnabled ? (
          <View
            style={{
              flex: desktop ? 1 : undefined,
              minWidth: 0,
              minHeight: 56,
              justifyContent: 'center',
              position: 'relative',
              zIndex: showReturnAutocomplete ? 41 : 2,
            }}
          >
            {showReturnField ? (
              showReturnAutocomplete ? (
                <GoogleAutocomplete
                  label="Return Location"
                  autoFocus
                  placeholder="Address, airport, city..."
                  onPress={(data: Address) => {
                    setDeliveryReturnToAddress({
                      description: data.description,
                      placeId: data.placeId,
                      latitude: data.latitude,
                      longitude: data.longitude,
                    });
                    setShowReturnAutocomplete(false);
                  }}
                />
              ) : (
                <HeroField
                  icon={<Location />}
                  label="Return Location"
                  value={deliveryReturnAddress?.description}
                  placeholder="Enter Address"
                  onPress={() => {
                    setErrorMsg('');
                    setShowStartCalendar(false);
                    setShowEndCalendar(false);
                    setShowAutocomplete(false);
                    setShowReturnAutocomplete(true);
                  }}
                />
              )
            ) : (
              <Pressable
                onPress={() => {
                  setShowReturnField(true);
                  setShowReturnAutocomplete(true);
                  setShowAutocomplete(false);
                  setShowStartCalendar(false);
                  setShowEndCalendar(false);
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: FIELD_ICON_GAP,
                  paddingVertical: 8,
                  // Stacked on mobile this reads as a row under the address, so
                  // indent it to the field text. On desktop it's its own column
                  // and lines up with the fields beside it instead.
                  paddingLeft: desktop ? FIELD_PADDING_H : FIELD_TEXT_OFFSET,
                  paddingRight: FIELD_PADDING_H,
                }}
              >
                <Icon
                  type="naked"
                  icon={<Plus />}
                  fillColor={theme.colors.text.secondary}
                  width={14}
                  height={14}
                />
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Return at different location
                </Text>
              </Pressable>
            )}
          </View>
        ) : null}

        {deliveryModeEnabled ? <FieldDivider /> : null}

        <View
          style={{
            flex: desktop ? 1 : undefined,
            minWidth: 0,
            position: 'relative',
            zIndex: showStartCalendar ? 40 : 2,
          }}
        >
          <HeroField
            icon={<CalendarIcon />}
            label="From"
            value={dateValue(startDatetime)}
            placeholder="Add date"
            active={showStartCalendar}
            onPress={() => {
              setShowAutocomplete(false);
              setShowEndCalendar(false);
              setShowStartCalendar(!showStartCalendar);
            }}
          />
          <Calendar
            isVisible={showStartCalendar}
            minDate={minDate}
            defaultStartDate={startDatetime}
            defaultEndDate={defaultEndDate}
            onHide={() => setShowStartCalendar(false)}
            onDatesSelected={(startDt: Date) => {
              // Mobile's Confirm button. Committing the From value is what
              // hands off to the To picker. This runs after onDateSelected, so
              // it has to re-apply the same clamp rather than undo it.
              const committed = startDt < minDate ? minDate : startDt;

              if (endDatetime && committed >= endDatetime) {
                setEndDatetime(addDays(committed, 3));
              }
              setStartDatetime(committed);
              setShowStartCalendar(false);
              setShowEndCalendar(true);
            }}
            onDateSelected={(startDt: Date) => {
              const formatted = setMinutes(
                setHours(startDt, getHours(startDatetime)),
                getMinutes(startDatetime)
              );
              if (formatted < minDate) {
                setStartDatetime(minDate);
                return;
              }
              if (endDatetime && formatted >= endDatetime) {
                setStartDatetime(formatted);
                setEndDatetime(addDays(formatted, 3));
                return;
              }
              setStartDatetime(formatted);
            }}
            onTimeSelected={(startDt: Date) => {
              if (endDatetime && startDt >= endDatetime) {
                setEndDatetime(addDays(startDt, 3));
              }
              setStartDatetime(startDt);

              // The desktop popover has no Confirm button, so picking a time
              // is the commit. Mobile has one, so leave the modal open until
              // it's pressed.
              if (desktop) {
                setShowStartCalendar(false);
                setShowEndCalendar(true);
              }
            }}
            range={false}
            inline
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 8,
              zIndex: 9999,
            }}
            containerStyle={calendarPopoverStyle}
            id="hero-start-calendar"
          />
        </View>

        <FieldDivider />

        <View
          style={{
            flex: desktop ? 1 : undefined,
            minWidth: 0,
            position: 'relative',
            zIndex: showEndCalendar ? 40 : 1,
          }}
        >
          <HeroField
            icon={<CalendarIcon />}
            label="To"
            value={dateValue(endDatetime)}
            placeholder="Add date"
            active={showEndCalendar}
            onPress={() => {
              setShowAutocomplete(false);
              setShowStartCalendar(false);
              setShowEndCalendar(!showEndCalendar);
            }}
          />
          <Calendar
            isVisible={showEndCalendar}
            minDate={addHours(startDatetime, 1)}
            defaultStartDate={endDatetime}
            onHide={() => setShowEndCalendar(false)}
            onDatesSelected={(endDt: Date) => {
              setEndDatetime(endDt);
              setShowEndCalendar(false);
            }}
            onDateSelected={(endDt: Date) => {
              setEndDatetime(
                setMinutes(
                  setHours(endDt, getHours(endDatetime)),
                  getMinutes(endDatetime)
                )
              );
            }}
            onTimeSelected={(endDt: Date) => {
              setEndDatetime(endDt);

              if (desktop) {
                setShowEndCalendar(false);
              }
            }}
            range={false}
            inline
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 8,
              zIndex: 9999,
            }}
            containerStyle={calendarPopoverStyle}
            id="hero-end-calendar"
            headerTitle="End Date"
          />
        </View>

        <Button
          text="Search"
          type="primary"
          onPress={onSearch}
          leftIcon={
            <Icon
              type="naked"
              icon={<SearchIcon />}
              fillColor={theme.colors.button.primary.default.text}
              width={16}
              height={16}
            />
          }
          style={{ width: desktop ? 120 : '100%' }}
        />
      </View>

      {deliveryModeEnabled && !desktop
        ? deliveryFeeLink({ alignSelf: 'flex-start' })
        : null}

      {errorMsg ? (
        <Text type="caption1" style={{ color: theme.colors.error }}>
          {errorMsg}
        </Text>
      ) : null}

      <DeliveryFeeModal
        visible={showFeeModal}
        onClose={() => setShowFeeModal(false)}
      />
    </View>
  );

  return (
    <View
      style={{
        width: '100%',
        minHeight: desktop ? 500 : 420,
        justifyContent: 'center',
        // Popovers (calendar/autocomplete) overflow the hero, so only clip when
        // nothing is open and lift the hero above the sections below when it is.
        overflow: anyDropdownOpen ? 'visible' : 'hidden',
        zIndex: anyDropdownOpen ? 30 : undefined,
      }}
    >
      <ImagePlaceholder
        source={
          UnistylesRuntime.themeName === 'dark'
            ? homeAssets.heroDark
            : homeAssets.heroLight
        }
        label="Hero background image"
        rounded={0}
        style={{ ...StyleSheetAbsoluteFill }}
      />

      <View
        style={{
          zIndex: 1,
          gap: 24,
          paddingHorizontal: 16,
          paddingVertical: 40,
          width: '100%',
          maxWidth: 1080,
          alignSelf: 'center',
        }}
      >
        <View style={{ gap: 8 }} pointerEvents="none">
          <Text
            type="largeTitle"
            style={{ textAlign: 'center', color: theme.colors.text.primary }}
          >
            Reserve your EV car
          </Text>
          <Text
            type="title2"
            style={{
              color: theme.colors.text.secondary,
              textAlign: 'center',
            }}
          >
            Premium Tesla and EV Rentals, delivered or ready to pick up.
          </Text>
        </View>

        {searchBar}

        <View style={{ gap: 12 }}>
          <Text
            type="bodyRegular"
            style={{
              color: theme.colors.text.secondary,
              textAlign: 'center',
            }}
          >
            Download our app for full control of the car.
          </Text>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Pressable
              onPress={() =>
                Linking.openURL(
                  'https://apps.apple.com/us/app/eon-instant-ev-rentals/id6448188911'
                )
              }
            >
              <Image
                source={homeAssets.downloadAppStore}
                contentFit="contain"
                style={{ width: 130, height: 44 }}
              />
            </Pressable>

            <Pressable
              onPress={() =>
                Linking.openURL(
                  'https://play.google.com/store/apps/details?id=com.eon.mobile'
                )
              }
            >
              <Image
                source={homeAssets.downloadGooglePlay}
                contentFit="contain"
                style={{ width: 147, height: 44 }}
              />
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
};

const StyleSheetAbsoluteFill = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
};
